<template>
  <div class="ol-company-info">
    <!-- En-tête entreprise -->
    <div class="company-header">
      <div v-if="business && business.logo" class="company-logo-large">
        <img :src="business.logo" alt="" />
      </div>
      <div v-else-if="business && business.name" class="company-logo-large company-logo-large--initials">
        {{ businessInitials }}
      </div>

      <h2 v-if="business && business.name" class="company-name-big">
        <a
          v-if="user.role_id == 1"
          class="company-name-link"
          :href="'/dashboard/tabs/19?b-name=' + business.name"
          target="_blank"
        >{{ business.name }}</a>
        <span v-else>{{ business.name }}</span>
      </h2>

      <p v-if="business && business.category_name" class="company-tagline">
        {{ business.category_name }}
      </p>

      <div class="company-tags">
        <span
          v-if="business_apollo && business_apollo.estimated_num_employees != null && business_apollo.estimated_num_employees !== ''"
          class="tag"
        >
          <i class="icon-users"></i>
          {{ business_apollo.estimated_num_employees }}
        </span>
        <span v-if="business && business.country" class="tag">
          <i class="icon-flag"></i>
          {{ business.country }}
        </span>
      </div>
    </div>

    <!-- À propos -->
    <div v-if="business && business.snippet" class="section">
      <div class="section-title">
        <i class="icon-info"></i>
        {{ $t("lang.opportunities.left_panel_about") }}
      </div>
      <p class="about-text" :class="{ collapsed: !aboutExpanded }">
        {{ aboutExpanded ? business.snippet : aboutSnippetPreview }}
      </p>
      <button
        v-if="business.snippet.length > aboutCharLimit"
        type="button"
        class="about-toggle"
        @click="aboutExpanded = !aboutExpanded"
      >
        {{ aboutExpanded ? $t("lang.opportunities.left_panel_read_less") : $t("lang.opportunities.left_panel_read_more") }}
        <i :class="aboutExpanded ? 'icon-angle-up' : 'icon-angle-down'"></i>
      </button>
    </div>

    <!-- Coordonnées -->
    <div
      v-if="hasCoordinatesSection"
      class="section"
    >
      <div class="section-title">
        <i class="icon-location"></i>
        {{ $t("lang.opportunities.left_panel_coordinates") }}
      </div>

      <div v-if="business && business.domain" class="info-row">
        <div class="info-icon">
          <i class="icon-desktop"></i>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("lang.label.website") }}</div>
          <div class="info-value">
            <a :href="'http://' + business.domain" target="_blank" rel="noopener noreferrer">{{ business.domain }}</a>
          </div>
        </div>
        <button
          type="button"
          class="action-btn-mini"
          :title="$t('lang.general.copytoclipboard')"
          @click="copyClipboard(business.domain)"
        >
          <i class="icon-copy"></i>
        </button>
      </div>

      <div v-if="business && business.address" class="info-row">
        <div class="info-icon">
          <i class="icon-location"></i>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("lang.label.address") }}</div>
          <div class="info-value info-value--wrap">{{ business.address }}</div>
        </div>
      </div>

      <div v-if="business && business.phone" class="info-row">
        <div class="info-icon">
          <i class="icon-phone"></i>
        </div>
        <div class="info-content">
          <div class="info-label">{{ $t("lang.opportunities.left_panel_standard_phone") }}</div>
          <div class="info-value">{{ business.phone }}</div>
        </div>
        <button
          type="button"
          class="action-btn-mini"
          :title="$t('lang.general.copytoclipboard')"
          @click="copyClipboard(business.phone)"
        >
          <i class="icon-copy"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { urlMixin, logoMixin } from "@/_mixins/";

export default {
  mixins: [urlMixin, logoMixin],
  props: {
    business: Object,
    business_apollo: Object,
  },
  data() {
    return {
      aboutExpanded: false,
      aboutCharLimit: 300,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.authentication.user,
    }),
    businessInitials() {
      if (!this.business || !this.business.name) return "";
      return this.business.name
        .split(/\s+/)
        .map((w) => w[0])
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },
    aboutSnippetPreview() {
      if (!this.business || !this.business.snippet) return "";
      if (this.business.snippet.length <= this.aboutCharLimit) {
        return this.business.snippet;
      }
      return this.business.snippet.substring(0, this.aboutCharLimit) + "...";
    },
    hasCoordinatesSection() {
      if (!this.business) return false;
      return !!(this.business.domain || this.business.address || this.business.phone);
    },
  },
  methods: {
    copyClipboard(text) {
      if (!text) return;
      if (window.clipboardData && window.clipboardData.setData) {
        return window.clipboardData.setData("Text", text);
      }
      if (document.queryCommandSupported && document.queryCommandSupported("copy")) {
        const textarea = document.createElement("textarea");
        textarea.textContent = text;
        textarea.style.position = "fixed";
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand("copy");
        } catch (ex) {
          console.warn("Copy to clipboard failed.", ex);
        } finally {
          document.body.removeChild(textarea);
        }
      }
    },
  },
};
</script>

<style scoped>
.ol-company-info {
  background: white;
  overflow: hidden;
}

.company-header {
  padding: 20px 18px 16px;
  text-align: center;
  border-bottom: 1px solid #f4f4f7;
}

.company-logo-large {
  width: 64px;
  height: 64px;
  border-radius: 14px;
  background: linear-gradient(135deg, #eeeefc 0%, #f7f7fe 100%);
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  overflow: hidden;
  font-size: 20px;
  font-weight: 600;
  color: #5054EB;
}

.company-logo-large img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 6px;
}

.company-name-big {
  font-size: 19px;
  font-weight: 500;
  color: #111827;
  margin: 0 0 4px;
  line-height: 1.2;
}

.company-name-link {
  color: inherit;
  text-decoration: none;
}

.company-name-link:hover {
  color: #5054EB;
}

.company-tagline {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 12px;
  line-height: 1.4;
}

.company-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
}

.tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 6px;
  background: #f4f4f7;
  color: #374151;
}

.tag i {
  font-size: 13px;
  color: #6b7280;
}

.country-flag {
  font-size: 13px;
  line-height: 1;
}

.section {
  padding: 14px 18px;
  border-bottom: 1px solid #f4f4f7;
}

.section-title {
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.section-title i {
  font-size: 13px;
}

.about-text {
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

.about-text.collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
}

.about-toggle {
  margin-top: 6px;
  background: none;
  border: none;
  color: #5054EB;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
  font-size: 13px;
}

.info-row + .info-row {
  border-top: 1px solid #f4f4f7;
}

.info-icon {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: #fafafb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.info-icon i {
  font-size: 14px;
  color: #6b7280;
}

.info-content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.info-label {
  font-size: 10px;
  color: #6b7280;
  font-weight: 500;
  line-height: 1.2;
}

.info-value {
  font-size: 13px;
  color: #111827;
  font-weight: 500;
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.info-value--wrap {
  white-space: normal;
  text-overflow: unset;
}

.info-value a {
  color: #5054EB;
  text-decoration: none;
}

.info-value a:hover {
  text-decoration: underline;
}

.action-btn-mini {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  background: white;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  flex-shrink: 0;
}

.action-btn-mini:hover {
  border-color: #5054EB;
  color: #5054EB;
  background: #f7f7fe;
}

.action-btn-mini i {
  font-size: 14px;
}
</style>
